import React, { useEffect, useState } from 'react';

import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectResult } from '../../components/select/select-chain.component';
import { SiTrackPage } from '../../components/si-track-page';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';

import { UserPermissionName } from '../../models/user-permission-name';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { invoiceReportApiKeyStore } from '../../api/invoice-report-api-key-store';
import useDebounce from '../../hooks/use-debounce';
import { State } from '@progress/kendo-data-query';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { WOReportRequestArgs, workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { GuardedComponent } from '../../components/guarded-component';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useFileDownload } from '../../components/file-download';
import { GenericFileUploadDialog } from '../../components/file-upload-dialog.component';
import { cocReportApiKeyStore } from '../../api/coc-report-api-key-store';
import ReportIcon from '@mui/icons-material/Report';
import { cocBillingReportColumns } from './coc-billing-report.columns';
import CocBillingReportFilter from './coc-billing-report.filter';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  workorder: any;
}

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

const CocBillingReportPage = () => {
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState<boolean>(false);
  const [unitID, setUnitID] = useState<any>();

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
    },
  });

  const { isLoading: isUploadFileLoading, mutateAsync: mutateUploadFileAsync } = useSaveData(
    cocReportApiKeyStore.uploadCocFile()
  );

  const [filtersState, setFiltersState] = useState<{ filters: any; trigger: number } | null>(null);

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
    cocReportApiKeyStore.getCocBillingReports(filtersState?.filters ?? {}),
    !!filtersState &&
    (filtersState.filters.customerID ||
      filtersState.filters.officeID ||
      filtersState.filters.projectID ||
      filtersState.filters.unitID ||
      filtersState.filters.municipalityId ||
      filtersState.filters.dateFrom ||
      filtersState.filters.dateTo ||
      filtersState.filters.cocStatus)
  );

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      customerID: !filtersData?.customer?.customerID ? 0 : filtersData?.customer?.customerID,
      officeID: !filtersData?.office?.officeID ? 0 : filtersData?.office?.officeID,
      projectID: !filtersData?.project?.projectID ? 0 : filtersData?.project?.projectID,
      unitID: !filtersData?.unit?.unitID ? 0 : filtersData?.unit?.unitID,
      dateFrom: filtersData?.fromDate,
      dateTo: filtersData?.toDate,
      municipalityId: !filtersData?.municipality?.municipalityID ? 0 : filtersData?.municipality?.municipalityID,
      cocStatus: !filtersData?.cocStatus?.coCStatusValue ? '' : filtersData?.cocStatus?.coCStatusValue,
    };

    // setFiltersState({ filters: filteringData, trigger: Date.now() });
    const prev = filtersState?.filters;

    const isSame =
      prev &&
      prev.customerID === filteringData.customerID &&
      prev.officeID === filteringData.officeID &&
      prev.projectID === filteringData.projectID &&
      prev.unitID === filteringData.unitID &&
      prev.dateFrom === filteringData.dateFrom &&
      prev.dateTo === filteringData.dateTo &&
      prev.municipalityId === filteringData.municipalityId &&
      prev.cocStatus === filteringData.cocStatus;
    if (isSame) {
      refetch();
    } else {
      setFiltersState({ filters: filteringData, trigger: Date.now() });
    }
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('CocBillingReport', 'CocBillingReport'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredCocReports, setFilteredCocReports] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredCocReportList = data?.data?.filter((CocReport: any) =>
      Object.values(CocReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredCocReports(filteredCocReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'CocBillingReport',
      pageName: 'CocBillingReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'CocBillingReport',
      pageName: 'CocBillingReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------
  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const handleDownloadFile = async (data: any) => {
    const value = {
      unitID: Number(data?.unitID) || 0,
      coCURL: data?.coCDownladURL ?? '',
      fileSection: 'cocfile',
      fileName: data?.coCDownloadfilename,
    };
    downloadFile({ additionalData: value });
  };
  const handleCoCSignedDownloadFile = async (data: any) => {
    const value = {
      unitID: Number(data?.unitID) || 0,
      coCURL: data?.coCSignedURL ?? '',
      fileSection: 'cocfile',
      fileName: data?.coCSignedDownloadfilename,
    };
    downloadFile({ additionalData: value });
  };

  const handleSaveCocFile = async (file: File | null) => {
    const formData = new FormData();
    formData.append('UnitID', unitID ?? '');
    formData.append('File', file ?? '');

    await mutateUploadFileAsync({ data: formData });
    refetch();
    setIsFileUploadDialogOpen(false);
  };

  const handleUploadClick = (cocData: any) => {
    setUnitID(cocData?.unitID);
    setIsFileUploadDialogOpen(true);
  };

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      window.open(`/units/profile/${workorder?.unitID}`, '_blank');
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  const FileDownloadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCDownladURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(cocData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <DownloadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };

  const FileUploadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCDownladURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleUploadClick(cocData)}>
              <Tooltip title={<Typography variant="body1">Upload</Typography>}>
                <FileUploadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <FileUploadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };

  const FileCoCSignedDownloadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCSignedURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleCoCSignedDownloadFile(cocData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <DownloadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };
  const StatusCell = ({ cocData }: { cocData: any }) => {
    const hasAlert = !!cocData?.cocAlertMessage;

    const formattedCocAlertMessage = cocData?.cocAlertMessage ? cocData?.cocAlertMessage?.replace(/\./g, '.\n') : '';
    return (
      <td>
        {hasAlert ? 'Request Pending' : cocData?.cocStatus}
        {hasAlert && (
          <Tooltip
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {formattedCocAlertMessage}
              </Typography>
            }
          >
            <ReportIcon style={{ color: 'orange', marginLeft: 4, verticalAlign: 'middle', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'jobsite') {
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'coCDownladURL') {
      return (dataItem: any) => {
        return <FileDownloadCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'coCUploadURL') {
      return (dataItem: any) => {
        return <FileUploadCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'cocStatus') {
      return (dataItem: any) => {
        return <StatusCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'coCSignedURL') {
      return (dataItem: any) => {
        return <FileCoCSignedDownloadCell cocData={dataItem.dataItem} />;
      };
    }
  };

  return (
    <div>
      {' '}
      <SiTrackPage title="CoC Billing Reports">
        <ReactHookForm formName="COC_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
          <Grid container spacing={1}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <CocBillingReportFilter />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Paper elevation={0} style={styling.paperStyle}>
                <SiTrackGrid
                  fileTitle='coc_billing_report'
                  columns={cocBillingReportColumns}
                  dataItemKey="unitID"
                  customCell={(field) =>
                    CustomGridCell({
                      field,
                    })
                  }
                  actionPermissions={{
                    export: UserPermissionName.DOWNLOAD_GRID,
                  }}
                  isBusy={isLoading || isFetching || isLoadingGridLayout || isFileDownloadLoading}
                  // -----------------Grid Layout-------
                  dataItems={filteredCocReports || []}
                  reorderable
                  handleSearch={handleInputChange}
                  handleSaveLayout={handleSaveLayout}
                  isSaveLayout={true}
                  gridConfigFromApi={gridConfig}
                  handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
              </Paper>
            </Grid>
          </Grid>
          {isFileUploadDialogOpen && (
            <GenericFileUploadDialog
              title="Upload CoC File"
              defaultData={{}}
              fileRequestArgs={{}}
              isLoading={isUploadFileLoading}
              onSubmit={(formData) => handleSaveCocFile(formData)}
              onClose={() => setIsFileUploadDialogOpen(false)}
              // acceptedFileTypes={{
              //   'text/csv': ['.csv'],
              //   'application/vnd.ms-excel': ['.xls'],
              //   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              // }}
              placeholder="UPLOAD FILE / DROP FILE HERE"
            />
          )}
        </ReactHookForm>
      </SiTrackPage>
    </div>
  );
};

export default CocBillingReportPage;
